import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '@headlessui/react';

import { loadUserPermissions, resetPermissions, selectUserPermissions, updatePermissionRecord } from '../../../../../state/store/reducers/entities/userPermissionSlice';
import useToast from '../../../../../state/hooks/useToast';
import { changePageInfo } from '../../../../../state/store/reducers/ui/headerSlice';
import usersPermissionsService from '../../../../../services/api/user-accounts/usersPermissionsService';
import { Button, ErrorLoading, ListNoData, Pagination } from '../../../../../components/common';
import { ListLoader } from '../../../../../components/loaders';
import { paginationInfo } from '../../../../../utils/pagination';
import PopoverWithFloat from '../../../../../components/hoc/PopoverWithFloat';
import ModalUserPermissionForm from '../modals/ModalUserPermissionForm';
import useAlert from '../../../../../state/hooks/useAlert';



function UserPermissionsListPage() {
    const dispatch = useDispatch();
    const userPermissions = useSelector(selectUserPermissions);
    const [searchParams, setSearchParams] = useSearchParams();

    const [userPermissionForm, setUserPermissionForm] = useState({ open: false, permission: null });

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    // get query params
    const page = Number(searchParams.get('page')) || userPermissions.pager.page;
    const searchFilters = {
        //name: searchParams.get('name') || '',
    };

    useEffect(() => {
        dispatch(changePageInfo({ title: "User Permissions" }));

        return () => {
            dispatch(resetPermissions({}));
        }
    }, []);


    useEffect(() => {
        getUserPermissions();
    }, [searchParams, dispatch]);



    const getUserPermissions = async () => {
        dispatch(resetPermissions({}));
        await dispatch<any>(loadUserPermissions({
            page: page,
            filters: searchFilters,
        }));
    };


    const handleDelete = (permission: any) => {
        openAlertConfirm({
            title: 'Delete Permission',
            message: `Are you sure you want to delete ${permission.name} permission?`,
            onConfirmed: () => deletePermission(permission),
            positiveButtonText: "Delete",
            negativeButtonText: 'Cancel',
            positiveButtonClass: 'bg-red-500 hover:bg-red-600',
        });
    }


    const deletePermission = async (permission: any) => {
        const result = await usersPermissionsService.deletePermission(permission.id);
        if (result.success) {
            getUserPermissions();
            addToast({
                text: "Permission deleted successfully",
                type: "success"
            });
        } else {
            addToast({
                title: "Error",
                text: "Permission could not be deleted",
                type: "error"
            });
        }
    }


    return (
        <div className=''>
            <div className='flex flex-row-reverse flex-wrap justify-between mb-1'>
                <Button type='button' onClick={() => setUserPermissionForm({ open: true, permission: null })} className='mb-4'>Add Permission</Button>
                <div>

                </div>
            </div>

            {
                // if there're permissions
                !userPermissions.loadingError && !userPermissions.loading && userPermissions.list.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 px-6'>Permission</th>
                                    <th className='py-6 px-6 text-right'>Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    userPermissions.list.map((permissionItem: any) =>
                                        <tr key={permissionItem.id}>
                                            <td className='py-4 px-6'>{permissionItem.name}</td>
                                            {/*  <td className='py-4 px-6 whitespace-nowrap'>{format(new Date(scannerItem.lastUsed), 'd LLL yyyy h:mm a')}</td> */}
                                            <td className='py-4 px-6 text-right'>
                                                <Menu as="div" className="relative inline-block text-left">
                                                    <PopoverWithFloat placement='bottom-end' portal>
                                                        <Menu.Button type='button' className={`w-9 h-9 transition-colors duration-300 bg-opacity-0 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ui-open:bg-black/5 dark:ui-open:bg-white/5`}>
                                                            <i className="ri-more-2-fill"></i>
                                                        </Menu.Button>

                                                        <Menu.Items as='div' unmount={false} className="w-64 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-gray-700/60 overflow-hidden px-2 py-3">
                                                            <Menu.Item as="button" type='button' onClick={() => handleDelete(permissionItem)} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-red-500 px-5 py-3">
                                                                Delete
                                                            </Menu.Item>
                                                        </Menu.Items>
                                                    </PopoverWithFloat>
                                                </Menu>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className='flex justify-between flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(userPermissions.pager.pageSize, userPermissions.pager.totalCount, page)}</p>

                        <Pagination
                            currentPage={page}
                            pageSize={userPermissions.pager.pageSize}
                            totalCount={userPermissions.pager.totalCount}
                            onPageChange={(page) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    page: page.toString(),
                                }));
                            }}
                        />
                    </div>
                </React.Fragment>
            }

            {
                // if permissions are loading
                !userPermissions.loadingError && userPermissions.loading &&
                <ListLoader loadingText='Loading User Permissions' className='px-8 py-24' />
            }

            {
                // if there're no permission in the app
                !userPermissions.loadingError && !userPermissions.loading && userPermissions.list.length === 0 && Object.keys(searchFilters).length === 0 &&
                <ListNoData title='No Permission Found' description='There are no user permissions added in the app' className='px-8 py-24'>
                    <Button type='button' onClick={() => setUserPermissionForm({ open: true, permission: null })}>Add a User Permission Here</Button>
                </ListNoData>
            }

            {
                // if there're no permission in the app
                !userPermissions.loadingError && !userPermissions.loading && userPermissions.list.length === 0 && Object.keys(searchFilters).length > 0 &&
                <ListNoData title='No User Permission Found' description='No permission was found for the selected scenarios' className='px-8 py-24' />
            }


            {
                userPermissions.loadingError &&
                <ErrorLoading title='Error Loading Permissions' message='There was a problem loading user permissions' className='px-8 py-24' onTryAgain={getUserPermissions} />
            }



            <ModalUserPermissionForm
                isOpen={userPermissionForm.open}
                permission={userPermissionForm.permission}
                onPermissionAdded={(permission) => {
                    addToast({
                        text: `Permission added successfully`,
                        type: "success"
                    });

                    dispatch(resetPermissions({}));
                    setSearchParams({});

                    if (Array.from(searchParams.entries()).length === 0) {
                        getUserPermissions();
                    }
                }}
                onPermissionUpdated={(permission) => {
                    dispatch(updatePermissionRecord({ permission, page }));
                    addToast({
                        text: `Permission updated successfully`,
                        type: "success"
                    });
                }}
                onClose={() => setUserPermissionForm((prevState) => ({ ...prevState, open: false }))}
            />
        </div>
    );
}


export default UserPermissionsListPage;